<template>
  <form @submit.prevent="save">
    <div class="form-group">
      <label>{{ $t("finding.target.extensionRequestCount") }}</label>
      <input type="number" v-model.number="form.extensionRequestCount" class="form-control" />
    </div>

    <hr />
    <div class="mb-2">
      <h6>{{ $t("finding.target.data") }}</h6>
    </div>

    <div class="table-responsive">
      <table class="table table-bordered">
        <thead>
          <tr>
            <th style="width: 20%">{{ $t("finding.target.criticality") + " *" }}</th>
            <th style="width: 30%">{{ $t("finding.target.description") }}</th>
            <th style="width: 25%">{{ $t("finding.target.approval-duration") + " *" }}</th>
            <th style="width: 25%">{{ $t("finding.target.resolution-duration") + " *" }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="key in ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']" :key="key">
            <td>
              <span class="font-weight-bold">{{ $t("audit-control.criticality." + key.toLowerCase()) }}</span>
            </td>
            <td>
              <sweet-input :disableValidation="true" v-model="form.criticality[key].description" layout="vertical" />
            </td>
            <td>
              <div class="d-flex">
                <input type="number" v-model="form.criticality[key].approvalDuration.value" class="form-control form-control-sm mr-1" placeholder="1" required />
                <select v-model="form.criticality[key].approvalDuration.unit" class="form-control form-control-sm">
                  <option value="WEEK">{{ $t("audit-control.unit.week") }}</option>
                  <option value="MONTH">{{ $t("audit-control.unit.month") }}</option>
                </select>
              </div>
            </td>
            <td>
              <div class="d-flex">
                <input type="number" v-model="form.criticality[key].resolutionDuration.value" class="form-control form-control-sm mr-1" placeholder="1" required />
                <select v-model="form.criticality[key].resolutionDuration.unit" class="form-control form-control-sm">
                  <option value="WEEK">{{ $t("audit-control.unit.week") }}</option>
                  <option value="MONTH">{{ $t("audit-control.unit.month") }}</option>
                </select>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="text-right mt-3">
      <button type="button" class="btn btn-secondary mr-2" @click="$emit('close')">{{ $t("common.cancel") }}</button>
      <button type="submit" class="btn btn-primary" :disabled="isUpdating">{{ $t("common.save") }}</button>
    </div>
  </form>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import SweetInput from "@c/form/sweet-input";
import SweetTextarea from "@c/form/sweet-textarea";

export default {
  components: {
    SweetInput,
    SweetTextarea,
  },
  data() {
    return {
      form: {
        extensionRequestCount: 2,
        criticality: this.getEmptyCriticality(),
      },
    };
  },
  computed: {
    ...mapGetters({
      editedEntity: "organizationFindingTarget/editedEntity",
      isUpdating: "organizationFindingTarget/isUpdating",
    }),
  },
  watch: {
    editedEntity: {
      deep: true,
      immediate: true,
      handler(val) {
        if (val && val._id) {
          const entity = this.prepareFormForEdit(val, []);

          // Normalize: migrate lowercase keys (legacy DB records) to uppercase
          // and apply prepareFormForEdit on each item's description field
          if (entity.criticality) {
            const normalized = {};
            Object.keys(entity.criticality).forEach((key) => {
              const upperKey = key.toUpperCase();
              // Apply prepareFormForEdit to extract description to current language string
              const item = this.prepareFormForEdit({ ...entity.criticality[key] }, ["description"]);
              if (!item.description || typeof item.description === "object") {
                item.description = "";
              }
              normalized[upperKey] = item;
            });
            entity.criticality = normalized;
          }

          // Fill in any missing keys with defaults
          const defaults = this.getEmptyCriticality();
          if (!entity.criticality) {
            entity.criticality = defaults;
          } else {
            ["LOW", "MEDIUM", "HIGH", "CRITICAL"].forEach((k) => {
              if (!entity.criticality[k]) entity.criticality[k] = defaults[k];
            });
          }

          this.form = { ...entity };
        } else {
          this.resetForm();
        }
      },
    },
  },
  methods: {
    ...mapActions({
      updateTarget: "organizationFindingTarget/save",
    }),
    getEmptyCriticality() {
      return {
        LOW: { description: "", approvalDuration: { value: 3, unit: "WEEK" }, resolutionDuration: { value: 1, unit: "MONTH" } },
        MEDIUM: { description: "", approvalDuration: { value: 2, unit: "WEEK" }, resolutionDuration: { value: 1, unit: "MONTH" } },
        HIGH: { description: "", approvalDuration: { value: 1, unit: "WEEK" }, resolutionDuration: { value: 2, unit: "WEEK" } },
        CRITICAL: { description: "", approvalDuration: { value: 1, unit: "WEEK" }, resolutionDuration: { value: 2, unit: "WEEK" } },
      };
    },
    resetForm() {
      this.form = {
        extensionRequestCount: 2,
        criticality: this.getEmptyCriticality(),
      };
    },
    async save() {
      const keys = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
      const invalid = keys.some((k) => !this.form.criticality[k] || !this.form.criticality[k].approvalDuration.value || !this.form.criticality[k].resolutionDuration.value);
      if (invalid) {
        this.$toast.warning(this.$t("common.validation-error"));
        return;
      }

      // Wrap description string back into Localized object before saving
      const payload = {
        ...this.form,
        criticality: {},
      };
      keys.forEach((k) => {
        const item = this.form.criticality[k];
        payload.criticality[k] = {
          ...item,
          description: { [this.currentLanguage]: item.description || "" },
        };
      });

      try {
        await this.$store.dispatch("organizationFindingTarget/save", payload);
        this.resetForm();
        this.$toast.success(this.$t("common.success"));
        this.$root.$emit("bv::toggle::collapse", "sidebar-new-target");
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
  },
};
</script>
